import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { file_path } from './environment';

const COLUMNS = [
    { key: 'id', label: 'ID', defaultVisible: false },
    { key: 'location', label: 'Location', defaultVisible: true },
    { key: 'practiceArea', label: 'Practice Area', defaultVisible: true },
    { key: 'practiceAreaDefinitionLink', label: 'Definition', defaultVisible: false, render: (val) => val ? <a href={val} target="_blank" rel="noreferrer">Link</a> : '' },
    {
        key: 'researcher',
        label: 'Researcher',
        defaultVisible: true,
        render: (val) => {
            const normalized = (val ?? '').toString().trim();
            if (!normalized || normalized.toLowerCase() === 'undefined' || normalized.toLowerCase() === 'null') {
                return '';
            }
            return <span style={{ whiteSpace: 'pre-line' }}>{normalized}</span>;
        }
    },
    { key: 'researcherNotes', label: 'Notes', defaultVisible: true },
    { key: 'status', label: 'Status', defaultVisible: true },
    { key: 'statusDate', label: 'Status Date', defaultVisible: true, render: (val) => val ? val.split('T')[0] : '' },
    { key: 'submissionKitFormLink', label: 'Submission Kit', defaultVisible: false, render: (val) => val ? <a href={val} target="_blank" rel="noreferrer">Link</a> : '' },
];

function defaultVisibility() {
    return Object.fromEntries(COLUMNS.map((c) => [c.key, c.defaultVisible]));
}

function ColumnToggles({ visible, onChange }) {
    return (
        <details className="column-toggle-panel">
            <summary>Choose visible columns</summary>
            <div className="column-toggles">
                {COLUMNS.map((col) => (
                    <label key={col.key} className="column-toggle-label">
                        <input
                            type="checkbox"
                            checked={visible[col.key] || false}
                            onChange={() => onChange(col.key)}
                        />
                        {col.label}
                    </label>
                ))}
            </div>
        </details>
    );
}

function ResearchTable({ rows }) {
    const [visible, setVisible] = useState(defaultVisibility);
    const [filters, setFilters] = useState({});

    const activeColumns = COLUMNS.filter((c) => visible[c.key]);

    function toggleColumn(key) {
        setVisible((prev) => ({ ...prev, [key]: !prev[key] }));
    }

    function handleFilterChange(event) {
        const { id, value } = event.target;
        setFilters((prev) => {
            const next = { ...prev };
            if (value) {
                next[id] = value;
            } else {
                delete next[id];
            }
            return next;
        });
    }

    const filteredRows = rows.filter((row) =>
        Object.entries(filters).every(([key, value]) => {
            const cell = row[key] ?? '';
            return cell.toString().toUpperCase().includes(value.toUpperCase());
        })
    );

    const filterableKeys = new Set(['location', 'practiceArea', 'researcher', 'status', 'researcherNotes']);

    return (
        <>
            <ColumnToggles visible={visible} onChange={toggleColumn} />
            <table id="schedule">
                <thead>
                    <tr>
                        {activeColumns.map((col) => (
                            <th key={col.key}>{col.label}</th>
                        ))}
                    </tr>
                    <tr>
                        {activeColumns.map((col) => (
                            <th key={col.key}>
                                {filterableKeys.has(col.key) ? (
                                    <input
                                        id={col.key}
                                        value={filters[col.key] || ''}
                                        onChange={handleFilterChange}
                                        placeholder="Search..."
                                    />
                                ) : null}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filteredRows.map((row) => (
                        <tr key={row.id}>
                            {activeColumns.map((col) => (
                                <td key={col.key}>
                                    {col.render ? col.render(row[col.key]) : row[col.key] ?? ''}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

export default function Research() {
    const [rows, setRows] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);
    const { guideId, year } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        axios.get(file_path('/research.json')).then((res) => {
            const guideEntry = Object.values(res.data).find(
                (g) => g.guideType === guideId
            );
            if (guideEntry) {
                setAvailableYears([...guideEntry.years].sort((a, b) => b - a));
            }
        });
    }, [guideId]);

    useEffect(() => {
        axios.get(file_path(`/research/${guideId}/${year}.json`)).then((res) => {
            setRows(res.data);
        });
    }, [guideId, year]);

    useEffect(() => {
        document.title = `${guideId} ${year} Research Schedule`;
    }, [guideId, year]);

    function handleYearChange(event) {
        navigate(`/research/${guideId}/${event.target.value}`);
    }

    return (
        <div className="body research-page">
            <div className="portal-links-row">
                <Link className="guides-return-link" to="/guides">Return to Portal</Link>
                <a className="guides-return-link report-request-link" href="mailto:mbuckley@gmail.com?subject=Research%20Page%20Bug%20or%20Feature%20Request">Report a bug or request a feature</a>
            </div>
            <h1>{guideId} {year} Research Schedule</h1>
            {availableYears.length > 1 && (
                <div className="research-history-control">
                    <label htmlFor="research-year">Available history</label>
                    <select id="research-year" value={year} onChange={handleYearChange}>
                        {availableYears.map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
            )}
            <ResearchTable rows={rows} />
        </div>
    );
}
